import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import { useAuth } from "../Auth/AuthContext";
import { FaBuilding, FaPlus, FaPause, FaPlay, FaUsers, FaSignInAlt } from "react-icons/fa";

export default function OrganizationsPage() {
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", admin_username: "", admin_email: "", admin_password: "" });
  const [saving, setSaving] = useState(false);

  const fetchOrgs = async () => {
    try {
      const { data } = await api.get("/admin/organizations");
      setOrgs(data);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Échec du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrgs(); }, []);

  const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  const createOrg = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/organizations", form);
      toast.success(`Organisation « ${form.name} » créée`);
      setForm({ name: "", slug: "", admin_username: "", admin_email: "", admin_password: "" });
      setShowForm(false);
      fetchOrgs();
    } catch (e2) {
      toast.error(e2?.response?.data?.error || "Échec de la création");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (org) => {
    const next = org.status === "active" ? "suspended" : "active";
    if (!window.confirm(`${next === "suspended" ? "Suspendre" : "Réactiver"} « ${org.name} » ?`)) return;
    try {
      await api.patch(`/admin/organizations/${org.id}/status`, { status: next });
      toast.success(`« ${org.name} » ${next === "suspended" ? "suspendue" : "réactivée"}`);
      fetchOrgs();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Échec");
    }
  };

  const enterOrg = async (org) => {
    try {
      const { data } = await api.post(`/admin/organizations/${org.id}/impersonate`);
      impersonate(data.token, { id: org.id, name: org.name, slug: org.slug });
      toast.success(`Vous consultez « ${org.name} »`);
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Impossible d'entrer dans l'org");
    }
  };

  if (loading) return <div className="flex justify-center h-48 items-center"><span className="loading loading-spinner loading-lg text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-base-content flex items-center gap-2"><FaBuilding className="text-primary" /> Organisations</h1>
          <p className="text-sm text-base-content/50 mt-0.5">Console SaaS — gestion des entreprises clientes.</p>
        </div>
        <button onClick={() => setShowForm((v) => !v)} className="btn btn-primary btn-sm gap-2"><FaPlus /> Nouvelle organisation</button>
      </div>

      {showForm && (
        <form onSubmit={createOrg} className="rounded-xl border border-base-200 p-5 grid gap-3 sm:grid-cols-2 animate-fade-in">
          <label className="text-sm">
            <span className="block text-base-content/60 mb-1">Nom *</span>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || slugify(e.target.value) })} className="input input-bordered input-sm w-full" />
          </label>
          <label className="text-sm">
            <span className="block text-base-content/60 mb-1">Slug *</span>
            <input required value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} className="input input-bordered input-sm w-full" placeholder="mon-entreprise" />
          </label>
          <label className="text-sm">
            <span className="block text-base-content/60 mb-1">Admin — identifiant *</span>
            <input required value={form.admin_username} onChange={(e) => setForm({ ...form, admin_username: e.target.value })} className="input input-bordered input-sm w-full" />
          </label>
          <label className="text-sm">
            <span className="block text-base-content/60 mb-1">Admin — email</span>
            <input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className="input input-bordered input-sm w-full" />
          </label>
          <label className="text-sm">
            <span className="block text-base-content/60 mb-1">Admin — mot de passe *</span>
            <input required type="password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} className="input input-bordered input-sm w-full" autoComplete="new-password" />
          </label>
          <div className="sm:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="btn btn-ghost btn-sm">Annuler</button>
            <button type="submit" disabled={saving} className="btn btn-primary btn-sm">{saving ? "…" : "Créer"}</button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto rounded-xl border border-base-300 shadow-sm">
        <table className="table table-zebra">
          <thead>
            <tr className="bg-base-200 text-base-content/70 text-xs uppercase tracking-wide">
              <th>ID</th><th>Nom</th><th>Slug</th><th>Users</th><th>Statut</th><th></th>
            </tr>
          </thead>
          <tbody>
            {orgs.length === 0 && (
              <tr><td colSpan={6} className="text-center text-base-content/40 py-6">Aucune organisation cliente.</td></tr>
            )}
            {orgs.map((o) => (
              <tr key={o.id} className="hover cursor-pointer" onClick={() => navigate(`/admin/organizations/${o.id}`)}>
                <td className="text-base-content/40">#{o.id}</td>
                <td className="font-medium">{o.name}</td>
                <td className="text-base-content/50">{o.slug}</td>
                <td><span className="inline-flex items-center gap-1 text-base-content/60"><FaUsers size={11} /> {o.user_count}</span></td>
                <td>
                  <span className={`badge badge-sm ${o.status === "active" ? "badge-success" : "badge-warning"}`}>{o.status === "active" ? "active" : "suspendue"}</span>
                </td>
                <td className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => enterOrg(o)} className="btn btn-xs btn-primary gap-1 mr-1">
                    <FaSignInAlt size={10} /> Entrer
                  </button>
                  <button onClick={() => toggleStatus(o)} className={`btn btn-xs gap-1 ${o.status === "active" ? "btn-ghost text-warning" : "btn-ghost text-success"}`}>
                    {o.status === "active" ? <><FaPause size={10} /> Suspendre</> : <><FaPlay size={10} /> Réactiver</>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
